import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { isAndroidNativeOnly } from '@/utils/platform-check';

// Use Documents directory instead of ExternalStorage to save to app files (not gallery)
// Files will be saved to: /Documents/app_images/[animation_folder]/
const APP_STORAGE_DIRECTORY = Directory.Documents;

// Request directory access using SAF
export async function requestDirectoryAccess() {
  if (!isAndroidNativeOnly()) {
    return { granted: true, uri: null };
  }

  try {
    // Use Capacitor's Filesystem plugin with SAF
    const result = await Filesystem.requestPermissions();

    if (result.publicStorage === 'granted') {
      return { granted: true, uri: result.uri };
    } else {
      return { granted: false, uri: null };
    }
  } catch (error) {
    console.error('Error requesting directory access:', error);
    return { granted: false, uri: null };
  }
}

// Download and save images to app files (not gallery)
export async function downloadImagesWithSAF(animationData) {
  const { afterImage, beforeImage, __component } = animationData;
  const folderPath = `app_images/${__component.replace('.', '/')}`;

  const imageJson = [
    { name: 'front', url: afterImage?.url },
    { name: 'back', url: beforeImage?.url },
  ];

  try {
    // Create app-specific folder (not in gallery)
    await Filesystem.mkdir({
      path: folderPath,
      directory: APP_STORAGE_DIRECTORY, // Use Documents directory
      recursive: true,
    });

    // Download and save each image
    for (const image of imageJson) {
      await downloadAndSaveImage(image, folderPath);
    }

    console.log(`Images saved to app files: ${folderPath}`);
    return true;
  } catch (error) {
    console.error('Error downloading images to app files:', error);
    return false;
  }
}

async function downloadAndSaveImage(image, folderPath) {
  try {
    // Fetch the image
    const response = await fetch(image.url, { cache: 'no-cache' });
    const blob = await response.blob();

    // Convert to base64
    const base64 = await blobToBase64(blob);

    // Save to app files (not gallery)
    await Filesystem.writeFile({
      path: `${folderPath}/${image.name}.jpg`,
      data: base64,
      directory: APP_STORAGE_DIRECTORY, // Use Documents directory
      encoding: Encoding.Base64,
    });

    console.log(`Image ${image.name} saved to app files successfully`);
  } catch (error) {
    console.error(`Error saving image ${image.name} to app files:`, error);
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Fallback to localStorage if SAF fails
export async function downloadImagesToLocalStorage(animationData) {
  const { afterImage, beforeImage, __component } = animationData;

  const imageData = {
    front: await fetchImageAsBase64(beforeImage?.url),
    back: await fetchImageAsBase64(afterImage?.url),
  };

  localStorage.setItem(animationData.__component.replace('.', '/'), JSON.stringify(imageData));

  return true;
}

async function fetchImageAsBase64(url) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await blobToBase64(blob);
  } catch (error) {
    console.error('Error fetching image:', error);
    return null;
  }
}

// Each platform words "that folder is not there" differently. iOS says the file "couldn't be
// opened because there is no such file" and Android says "Directory does not exist"; neither
// contains "not found", so both were logged as failures (~1,500 a week in production).
const MISSING_DIRECTORY_MESSAGES = ['not found', 'no such file', 'does not exist'];

export function isMissingDirectoryError(error) {
  const message = error?.message?.toLowerCase() ?? '';
  return MISSING_DIRECTORY_MESSAGES.some(text => message.includes(text));
}

// Remove directory from app files
export async function removeDirectoryWithSAF(animationData) {
  const { __component } = animationData;
  const folderPath = `app_images/${__component.replace('.', '/')}`;

  try {
    await Filesystem.rmdir({
      path: folderPath,
      directory: APP_STORAGE_DIRECTORY, // Use Documents directory
      recursive: true,
    });
    console.log('App files directory removed successfully');
    return false; // Success
  } catch (error) {
    if (isMissingDirectoryError(error)) {
      // Directory does not exist yet — expected on first run, nothing to remove
      return false;
    }
    console.error('Failed to remove app files directory:', error);
    return true; // Error
  }
}

// Get all images from app files directory
export async function getAllImagesFromSAF(folderName) {
  try {
    const result = await Filesystem.readdir({
      path: `app_images/${folderName}`,
      directory: APP_STORAGE_DIRECTORY, // Use Documents directory
    });
    return result.files;
  } catch (error) {
    console.error('Error retrieving images from app files:', error);
    return [];
  }
}

// Read file from app files directory
export async function readImageFileFromSAF(folderName, filePath) {
  try {
    const result = await Filesystem.readFile({
      path: `app_images/${folderName}/${filePath}`,
      directory: APP_STORAGE_DIRECTORY, // Use Documents directory
      encoding: Encoding.Base64,
    });
    return result.data;
  } catch (error) {
    console.error('Error reading file from app files:', error);
    return null;
  }
}
